import React, { useState, useRef } from "react";
import { X, ShieldAlert, CheckCircle, Paperclip, Image, Film, XCircle } from "lucide-react";
import { motion } from "framer-motion";
import api from "../../api/axios";
import toast from "react-hot-toast";

export default function ReportModal({ isOpen, onClose, reportedId, messageId }) {
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [attachedFile, setAttachedFile] = useState(null);
    const fileInputRef = useRef(null);

    const REASONS = [
        { id: "HARASSMENT", label: "Harassment or Bullying" },
        { id: "HATE_SPEECH", label: "Hate Speech" },
        { id: "SEXUAL", label: "Sexual Solicitation / Inappropriate" },
        { id: "PREDATORY", label: "Predatory Behavior / Grooming" },
        { id: "SELF_HARM", label: "Self-Harm or Suicide Risk" },
        { id: "SPAM", label: "Spam or Scam" },
        { id: "LGBT+ Family", label: "LGBT+ Family" },
    ];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const isValid = file.type.startsWith("image/") || file.type.startsWith("video/");
        if (!isValid) {
            toast.error("Only images and video recordings are allowed.");
            return;
        }
        if (file.size > 20 * 1024 * 1024) { // 20MB limit
            toast.error("File is too large. Maximum 20MB allowed.");
            return;
        }
        setAttachedFile(file);
    };

    const handleSubmit = async () => {
        if (!reason) return toast.error("Please select a reason");

        // Guard: If reportedId is null (e.g. your own message), show a clear error
        if (!reportedId) {
            return toast.error("Cannot identify the user to report. Please try reporting from the other person's message.");
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("reportedId", reportedId);
            formData.append("reason", reason);
            if (messageId) formData.append("messageId", messageId);
            if (attachedFile) formData.append("evidence", attachedFile);

            await api.post("/moderation/report", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setSuccess(false);
                setReason("");
                setAttachedFile(null);
            }, 2000);
        } catch (err) {
            const msg = err?.response?.data?.error || "Failed to submit report";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-red-100 dark:border-red-900/30"
            >
                {success ? (
                    <div className="p-8 flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400">
                            <CheckCircle className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Report Submitted</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            Thank you for keeping our community safe. We will review this immediately.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-red-50/50 dark:bg-red-900/10">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <ShieldAlert className="w-5 h-5" />
                                <h3 className="font-semibold">Report Content</h3>
                            </div>
                            <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Why are you reporting this? Your report is anonymous and safe.
                            </p>

                            {/* Reason Selector */}
                            <div className="space-y-2">
                                {REASONS.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => setReason(r.id)}
                                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm font-medium ${reason === r.id
                                            ? "border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-500"
                                            : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-200 dark:hover:border-red-800"
                                            }`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>

                            {/* Evidence Attachment */}
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium uppercase tracking-wide">
                                    Attach Evidence (optional)
                                </p>
                                {attachedFile ? (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                        {attachedFile.type.startsWith("image/")
                                            ? <Image className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                            : <Film className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                        }
                                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">{attachedFile.name}</span>
                                        <button onClick={() => setAttachedFile(null)} className="text-gray-400 hover:text-red-400 flex-shrink-0">
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 hover:border-red-300 dark:hover:border-red-700 hover:text-red-500 transition-colors"
                                    >
                                        <Paperclip className="w-4 h-4" />
                                        Add screenshot or screen recording
                                    </button>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,video/*"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!reason || loading}
                                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-lg shadow-red-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loading ? "Submitting..." : "Submit Report"}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
